import { useMemo } from 'react';
import {
  CartesianGrid, Line, LineChart, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Text } from '@mantine/core';
import { AttendanceStatus, TrainingEvent } from '../types/training-event.type';
import dayjs from 'dayjs';

export type AttendanceMetric = 'attendance' | 'excused' | 'unexcused';

export const METRIC_LABELS: Record<AttendanceMetric, string> = {
  attendance: 'Anwesenheit %',
  excused: 'Entschuldigt %',
  unexcused: 'Unentschuldigt %',
};

const METRIC_COLORS: Record<AttendanceMetric, string> = {
  attendance: '#2f9e44',
  excused: '#f08c00',
  unexcused: '#e03131',
};

interface ChartPoint {
  label: string;
  value: number;
  present: number;
  excused: number;
  unexcused: number;
  total: number;
}

function buildPoint(event: TrainingEvent, metric: AttendanceMetric, personId?: number | null): ChartPoint | null {
  const all = event.participants ?? [];
  const pool = personId ? all.filter((p) => p.person?.id === personId) : all;
  if (pool.length === 0) return null;

  const present   = pool.filter((p) => p.attendanceStatus === AttendanceStatus.PRESENT).length;
  const excused   = pool.filter((p) => p.attendanceStatus === AttendanceStatus.EXCUSED).length;
  const unexcused = pool.filter((p) => p.attendanceStatus === AttendanceStatus.UNEXCUSED).length;
  const total = pool.length;

  const numerator = metric === 'attendance' ? present : metric === 'excused' ? excused : unexcused;
  const value = Math.round((numerator / total) * 100);

  return { label: dayjs(event.date).format('DD.MM.'), value, present, excused, unexcused, total };
}

// recharts tooltip generics are intentionally bypassed with `any` — the payload
// shape is our own ChartPoint regardless of ValueType/NameType inference.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload, metric }: { active?: boolean; payload?: any[]; metric: AttendanceMetric }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload as ChartPoint;
  return (
    <div style={{ background: 'var(--mantine-color-body)', border: '1px solid var(--mantine-color-default-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
      <div><strong>{d.label}</strong></div>
      <div style={{ color: '#2f9e44' }}>Anwesend: {d.present}</div>
      <div style={{ color: '#f08c00' }}>Entschuldigt: {d.excused}</div>
      <div style={{ color: '#e03131' }}>Unentschuldigt: {d.unexcused}</div>
      <div style={{ color: '#868e96' }}>Gesamt: {d.total}</div>
      <div style={{ marginTop: 4, fontWeight: 600, color: METRIC_COLORS[metric] }}>
        {METRIC_LABELS[metric]}: {d.value}%
      </div>
    </div>
  );
}

interface Props {
  events: TrainingEvent[];
  metric: AttendanceMetric;
  personId?: number | null;
  height?: number;
}

export default function AttendanceChart({ events, metric, personId, height = 220 }: Props) {
  const data = useMemo(() => {
    const sorted = [...events].sort((a, b) => {
      const d = a.date.localeCompare(b.date);
      return d !== 0 ? d : a.startTime - b.startTime;
    });
    return sorted
      .map((e) => buildPoint(e, metric, personId))
      .filter((p): p is ChartPoint => p !== null);
  }, [events, metric, personId]);

  if (data.length === 0) {
    return <Text c="dimmed" size="sm" ta="center" py="md">Keine Daten für diesen Filter</Text>;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--mantine-color-default-border)" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Tooltip content={(props: any) => <CustomTooltip {...props} metric={metric} />} />
        <ReferenceLine y={80} stroke="#868e96" strokeDasharray="4 4" label={{ value: '80%', fontSize: 10, fill: '#868e96' }} />
        <Line
          type="monotone"
          dataKey="value"
          stroke={METRIC_COLORS[metric]}
          strokeWidth={2}
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
